/**
 * ndhLookup.js — live lookup against CMS's National Directory of
 * Healthcare Providers & Services (NPD / NDH).
 *
 * TWO SOURCES, because they are not the same data:
 *
 *  1. NPD search API — GET https://directory.cms.gov/api/search/practitioners/{npi}
 *     The exact JSON the public NPD provider page (directory.cms.gov/provider/{npi})
 *     renders from. This is the PRIMARY source: it is the only place
 *     that has provider-level endpoints (URL, FHIR version, HTTP
 *     response, response time, connection type, information source),
 *     data-network participation, licenses, languages, telehealth,
 *     board certifications, DEA/CLIA, NPD attestation status, the
 *     primary-specialty flag, and the affiliated-organization names the
 *     website shows. The FHIR API is missing or disagrees on all of
 *     these — e.g. for NPI 1154322766, FHIR's Organization-1831115641
 *     is "CRANEWARE INC" (a record with 2,852 addresses) and the
 *     PractitionerRole is active:false, while the website shows an
 *     active affiliation with "OZARKS MEDICAL CENTER".
 *
 *  2. FHIR R4 — https://directory.cms.gov/fhir. Still used for the
 *     "care team at this location" graph (needs FHIR Location ids,
 *     which the search API doesn't expose), the Organization.partOf
 *     ownership chain, the HHS exclusion-list flag, and as a full
 *     fallback if the search API is unreachable.
 *
 * CORS/proxy: despite the CapabilityStatement's `rest[0].security`
 * block claiming `"cors": true`, real end-to-end testing (see
 * npi_proxy_test.py) found a proxy genuinely is needed — direct
 * browser calls don't reliably work. A permanent proxy route
 * (NpiDirectoryController.cs) is planned for the real .NET backend
 * but doesn't exist yet, so this currently points at a small,
 * temporary standalone proxy (deployed from npi_proxy_test.py) via
 * REACT_APP_NDH_PROXY_URL. Swap this back to the real backend's route
 * once NpiDirectoryController.cs ships — this file shouldn't need any
 * other change when that happens, just the URL.
 *
 * Only maps fields that are actually present in real API responses.
 * Nothing is fabricated — if a field isn't in the FHIR resource, the
 * parsed object carries `null`/an empty array for it rather than a guess.
 */

// Points at whatever proxy is currently fronting the CMS directory —
// a temporary standalone proxy (npi_proxy_test.py) today, the real
// backend's NpiDirectoryController.cs once that exists. Falls back to
// localhost:5000 for local dev when the env var isn't set.
const NDH_BASE_URL = process.env.REACT_APP_NDH_PROXY_URL || "http://localhost:5000/npi-directory";
// The NPD search API route on the same proxy (see npi_proxy_test.py).
// Derived from the FHIR proxy URL so existing deployments only need the
// one env var; override with REACT_APP_NPD_API_URL if hosted elsewhere.
const NPD_API_BASE_URL =
  process.env.REACT_APP_NPD_API_URL || NDH_BASE_URL.replace(/\/npi-directory\/?$/, "/npd-api");
const NPI_IDENTIFIER_SYSTEM = "http://terminology.hl7.org/NamingSystem/npi";
const FHIR_HEADERS = { Accept: "application/fhir+json" };

function toTitleCase(str) {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

// "HUSSEIN A SAAD" (family/given from the API, all caps) -> "Hussein A. Saad"
function formatPractitionerName(nameEntry) {
  if (!nameEntry) return "";
  const given = (nameEntry.given || [])
    .map(toTitleCase)
    .map((g) => (g.length === 1 ? `${g}.` : g));
  const family = toTitleCase(nameEntry.family || "");
  return [...given, family].filter(Boolean).join(" ");
}

// extension.url values are long, versioned URLs — match on a stable
// fragment rather than the exact string so this doesn't silently break
// if CMS revs the profile version.
function extractFlag(extensions, urlFragment) {
  const ext = (extensions || []).find((e) => e.url && e.url.includes(urlFragment));
  return ext ? !!ext.valueBoolean : null; // null = this extension wasn't present at all
}

function referenceId(ref) {
  // "Organization/Organization-123" -> "Organization-123"
  if (!ref) return null;
  const parts = String(ref).split("/");
  return parts[parts.length - 1] || null;
}

// Pulls just the credential string off a Practitioner resource's
// qualification array — same rule parsePractitionerResource uses for
// the main doctor, factored out so fetchCareTeamAtLocation can give
// every other real person on the team a real credential too, instead
// of only ever knowing the primary doctor's.
//
// FaCeT-credentialCS is a real FHIR CodeSystem, so like any of them
// its `code` is the short machine value (MD, DO, NP, PA-C...) and
// `display` is the long human-readable name (Doctor of Medicine,
// Doctor of Osteopathy...). This used to prefer `display`, which is
// exactly what was overflowing the care-team graph's node labels —
// switched to prefer the real short `code` instead, still falling
// back to `display` for any qualification whose code is missing.
function extractCredential(qualifications) {
  for (const q of qualifications || []) {
    const coding = q.code?.coding?.[0];
    if (coding?.system === "http://hl7.org/fhir/us/ndh/CodeSystem/FaCeT-credentialCS") {
      return coding.code || coding.display || "";
    }
  }
  return "";
}

function parsePractitionerResource(resource) {
  if (!resource) return null;

  const npiIdentifier = (resource.identifier || []).find(
    (id) => id.system === NPI_IDENTIFIER_SYSTEM
  );

  const specialties = [];
  let credential = "";
  let stateLicense = null;

  (resource.qualification || []).forEach((q) => {
    const coding = q.code?.coding?.[0];
    if (!coding) return;

    if (coding.system === "http://hl7.org/fhir/us/ndh/CodeSystem/FaCeT-credentialCS") {
      // Same short-code-first fix as extractCredential() above, kept
      // consistent with the NPD REST path's own credential field
      // (parseNpdProvider, below: "credential.code || credential.name").
      credential = coding.code || coding.display || "";
      return;
    }

    if (coding.system === "http://hl7.org/fhir/us/ndh/ValueSet/HealthcareIndividualTaxonomyVS") {
      if (q.identifier && q.identifier.length) {
        // This particular qualification entry carries a real license
        // number — it's the state-license record, not a plain specialty.
        const licenseId = q.identifier[0];
        const issuerRef = q.issuer?.reference || "";
        const stateMatch = issuerRef.match(/Organization-State-([A-Za-z]{2})/);
        stateLicense = {
          number: licenseId.value,
          state: stateMatch ? stateMatch[1].toUpperCase() : null,
          taxonomyDisplay: coding.display || q.code.text || "",
          // Genuinely present in FHIR (Qualification.period) but CMS
          // frequently just doesn't populate it — render honestly as
          // "not provided" when absent rather than hiding the field.
          issued: q.period?.start || null,
          expires: q.period?.end || null,
        };
      } else {
        specialties.push({
          code: coding.code,
          display: coding.display || q.code.text || coding.code,
        });
      }
    }
  });

  const telecom = resource.telecom || [];
  const phones = telecom.filter((t) => t.system === "phone").map((t) => t.value);
  const faxes = telecom.filter((t) => t.system === "fax").map((t) => t.value);

  return {
    id: resource.id, // e.g. "Practitioner-1235339011" — needed to query PractitionerRole next
    npi: npiIdentifier?.value || (resource.id || "").replace("Practitioner-", ""),
    enumerationDate: npiIdentifier?.period?.start
      ? npiIdentifier.period.start.slice(0, 10)
      : null,
    active: resource.active !== false,
    name: formatPractitionerName(resource.name?.[0]) || "Unknown provider",
    gender: resource.gender || null,
    credential,
    specialties,
    stateLicense,
    addresses: (resource.address || []).map((a) => ({
      line: (a.line || []).join(", "),
      city: a.city || "",
      state: a.state || "",
      postalCode: a.postalCode || "",
    })),
    phones,
    faxes,
    flags: {
      identityVerified: extractFlag(resource.extension, "identity-verified"),
      medicareEnrolled: extractFlag(resource.extension, "medicare_enrollment"),
      exclusionListed: extractFlag(resource.extension, "in-exclusion-list"),
      dataNetworkAligned: extractFlag(resource.extension, "aligned_with_data_network"),
    },
    practices: [], // filled in by fetchProviderFromNdh, best-effort
    endpoints: [], // filled in by fetchProviderFromNdh, best-effort
    lastUpdated: resource.meta?.lastUpdated || null,
    // The public, human-readable NDH profile page (what a person actually
    // wants to open) — not the FHIR API query URL, which just returns raw
    // JSON. Same shape for every provider: directory.cms.gov/provider/{npi}.
    rawRecordUrl: npiIdentifier?.value
      ? `https://directory.cms.gov/provider/${npiIdentifier.value}`
      : null,
  };
}

// Turns a PractitionerRole search Bundle (with organization + location
// _include'd, as confirmed supported by the CapabilityStatement) into a
// clean list of "practices" — one per PractitionerRole, each with its
// resolved organization and location(s) already attached.
function parsePracticesBundle(bundle) {
  const entries = bundle?.entry || [];
  const orgById = {};
  const locById = {};
  const roles = [];

  entries.forEach((e) => {
    const res = e?.resource;
    if (!res) return;
    if (res.resourceType === "Organization") orgById[res.id] = res;
    else if (res.resourceType === "Location") locById[res.id] = res;
    else if (res.resourceType === "PractitionerRole") roles.push(res);
  });

  return roles.map((role) => {
    const orgId = referenceId(role.organization?.reference);
    const org = orgId ? orgById[orgId] : null;
    const orgNpi = (org?.identifier || []).find(
      (id) => id.system === NPI_IDENTIFIER_SYSTEM
    )?.value;

    const locations = (role.location || [])
      .map((l) => locById[referenceId(l.reference)])
      .filter(Boolean)
      .map((loc) => {
        const locTelecom = loc.telecom || [];
        return {
          // Real FHIR `id` — needed to query "who else has a
          // PractitionerRole at this Location" (PractitionerRole?
          // location=Location/{id}), confirmed a real, documented
          // search param on the CapabilityStatement. Without this we
          // could only ever see the current doctor's own affiliations,
          // never other real people/orgs actually sharing the address.
          id: loc.id || null,
          name: loc.name || "",
          line: (loc.address?.line || []).join(", "),
          city: loc.address?.city || "",
          state: loc.address?.state || "",
          postalCode: loc.address?.postalCode || "",
          // Location resources can carry their own phone/fax, separate
          // from the Practitioner's — this was being fetched from CMS
          // but never actually attached to the location object, so it
          // silently never reached the UI whenever a doctor had any
          // practice location on file (the common case).
          phone: locTelecom.find((t) => t.system === "phone")?.value || null,
          fax: locTelecom.find((t) => t.system === "fax")?.value || null,
          // Real FHIR field (Location.position) — powers the map embed.
          // Absent for plenty of locations; every consumer of this must
          // treat it as optional and skip the map gracefully, not fake
          // coordinates.
          latitude: typeof loc.position?.latitude === "number" ? loc.position.latitude : null,
          longitude: typeof loc.position?.longitude === "number" ? loc.position.longitude : null,
        };
      });

    const newPatientsExt = (role.extension || []).find((e) => e.url && e.url.includes("newpatients"));
    const acceptingCoding = (newPatientsExt?.extension || []).find((e) => e.url === "acceptingPatients")
      ?.valueCodeableConcept?.coding?.[0];

    return {
      id: role.id,
      active: role.active !== false,
      // FHIR base-ext-newpatients, e.g. { code: "newpt", display: "Accepting new and existing patients" }
      acceptingPatients: acceptingCoding
        ? { code: acceptingCoding.code || null, display: acceptingCoding.display || acceptingCoding.code || null }
        : null,
      organizationId: orgId,
      organizationName: org?.name || "Unknown organization",
      organizationNpi: orgNpi || null,
      // Real, standard FHIR field (Organization.type) — a genuinely
      // optional field on the same Organization resource we already
      // fetch, not something extra to query for. Only ever shown when
      // actually present on the record (e.g. "equipment supplier");
      // never guessed at when CMS didn't report it.
      organizationType: org?.type?.[0]?.coding?.[0]?.display || org?.type?.[0]?.text || null,
      // Real FHIR field (Organization.partOf) — a documented search
      // param on the CapabilityStatement ("Parent organization
      // (hierarchy and oversized-record parts)"). Resolved into an
      // actual chain of parent names in fetchOwnershipChain below —
      // this is just the raw reference to that parent, captured here
      // since it's already sitting on the Organization resource we
      // already fetched, no extra request needed for this part.
      organizationPartOfRef: org?.partOf?.reference || null,
      locations,
    };
  });
}

// Walks Organization.partOf up the real hierarchy — a documented,
// confirmed-real search param/field on the CapabilityStatement, not a
// guess. Each hop is one extra GET (a single Organization read, not a
// search), capped at 5 levels as a sane ceiling against any unexpected
// reference cycle. Returns [] if the org has no parent at all — that's
// a genuine, verified "operates independently" finding, not a failure.
async function fetchOwnershipChain(initialPartOfRef) {
  const chain = [];
  let ref = initialPartOfRef;
  let hops = 0;

  while (ref && hops < 5) {
    hops += 1;
    const id = referenceId(ref);
    if (!id) break;
    try {
      const resp = await fetch(`${NDH_BASE_URL}/Organization/${id}`, { headers: FHIR_HEADERS });
      if (!resp.ok) break;
      const org = await resp.json();
      chain.push({
        id: org.id,
        name: org.name || "Unknown organization",
        npi: (org.identifier || []).find((i) => i.system === NPI_IDENTIFIER_SYSTEM)?.value || null,
      });
      ref = org.partOf?.reference || null;
    } catch {
      break;
    }
  }

  return chain;
}

// Real "who else practices here" — queries PractitionerRole?location=
// directly (a documented, confirmed-real search param), which returns
// every PractitionerRole at that address, not just the current
// doctor's own. That's a meaningfully different (and more accurate)
// question than grouping the current doctor's own practices by
// address, which is all the earlier version of this feature did.
async function fetchCareTeamAtLocation(locationId, excludeNpi) {
  if (!locationId) return [];
  try {
    const url =
      `${NDH_BASE_URL}/PractitionerRole?location=${encodeURIComponent(`Location/${locationId}`)}` +
      `&active=true&_count=100` +
      `&_include=PractitionerRole:practitioner&_include=PractitionerRole:organization`;
    const resp = await fetch(url, { headers: FHIR_HEADERS });
    if (!resp.ok) return [];
    const bundle = await resp.json();

    const practitionerById = {};
    const orgById = {};
    const roles = [];
    (bundle.entry || []).forEach((e) => {
      const res = e?.resource;
      if (!res) return;
      if (res.resourceType === "Practitioner") practitionerById[res.id] = res;
      else if (res.resourceType === "Organization") orgById[res.id] = res;
      else if (res.resourceType === "PractitionerRole") roles.push(res);
    });

    const seen = new Set();
    const team = [];
    roles.forEach((role) => {
      if (role.active === false) return;
      const practitionerId = referenceId(role.practitioner?.reference);
      const practitioner = practitionerId ? practitionerById[practitionerId] : null;
      if (!practitioner) return;
      const npi = (practitioner.identifier || []).find((i) => i.system === NPI_IDENTIFIER_SYSTEM)?.value;
      if (!npi || npi === excludeNpi || seen.has(npi)) return;
      seen.add(npi);
      const orgId = referenceId(role.organization?.reference);
      const org = orgId ? orgById[orgId] : null;
      team.push({
        npi,
        name: formatPractitionerName(practitioner.name?.[0]) || "Unknown provider",
        // Real credential per team member, not just the primary doctor —
        // powers the "Dr. HA, MD" style label instead of a bare hover-only
        // name with no letters after it.
        credential: extractCredential(practitioner.qualification),
        organizationName: org?.name || null,
        // Real FHIR field (PractitionerRole.specialty) — a documented
        // search param on the CapabilityStatement ("Practitioner
        // specialty"). Only shown when CMS actually reports it; never
        // guessed at from the organization name or anything else.
        specialty: role.specialty?.[0]?.coding?.[0]?.display || role.specialty?.[0]?.text || null,
      });
    });

    return team;
  } catch {
    return [];
  }
}

function parseEndpointBundle(bundle) {
  const entries = bundle?.entry || [];
  return entries
    .map((e) => e?.resource)
    .filter((res) => res?.resourceType === "Endpoint")
    .map((res) => ({
      id: res.id,
      name: res.name || res.connectionType?.display || "Endpoint",
      connectionType: res.connectionType?.display || res.connectionType?.code || "",
      address: res.address || "",
      status: res.status || "unknown",
    }));
}

// Best-effort: fetches PractitionerRole (+ organization/location),
// endpoints for each unique organization, the real ownership chain for
// the primary organization (Organization.partOf), and the real care
// team at the primary location (PractitionerRole?location=). Every
// step here is wrapped so a failure just means an empty/absent result
// for that one piece — it should never take down the core provider
// lookup, since all of this is additive detail, not the primary record.
async function fetchPracticesAndEndpoints(
  practitionerId,
  npi,
  preferredAddressLine = null,
  { includeEndpoints = true } = {}
) {
  const result = {
    practices: [],
    endpoints: [],
    ownershipChain: [],
    careTeam: [],
    careTeamLocation: null,
  };
  if (!practitionerId) return result;

  try {
    const rolesUrl =
      `${NDH_BASE_URL}/PractitionerRole?practitioner=${encodeURIComponent(
        `Practitioner/${practitionerId}`
      )}&_include=PractitionerRole:organization&_include=PractitionerRole:location`;

    const rolesResp = await fetch(rolesUrl, { headers: FHIR_HEADERS });
    if (!rolesResp.ok) return result;

    const rolesBundle = await rolesResp.json();
    result.practices = parsePracticesBundle(rolesBundle);

    const uniqueOrgIds = [
      ...new Set(result.practices.map((p) => p.organizationId).filter(Boolean)),
    ];

    if (includeEndpoints && uniqueOrgIds.length) {
      const endpointResults = await Promise.allSettled(
        uniqueOrgIds.map((orgId) =>
          fetch(
            `${NDH_BASE_URL}/Endpoint?organization=${encodeURIComponent(
              `Organization/${orgId}`
            )}`,
            { headers: FHIR_HEADERS }
          ).then((r) => (r.ok ? r.json() : null))
        )
      );
      endpointResults.forEach((r) => {
        if (r.status === "fulfilled" && r.value) {
          result.endpoints.push(...parseEndpointBundle(r.value));
        }
      });
    }

    // Both of these key off the *primary* practice — the one with the
    // most affiliated entities, same "primary" definition already used
    // everywhere else this data shows up (ProviderQuickLook's old
    // primaryLocation, NpdDetailsTab's primaryLocation).
    const primaryPractice = result.practices[0];
    if (primaryPractice?.organizationPartOfRef) {
      result.ownershipChain = await fetchOwnershipChain(primaryPractice.organizationPartOfRef);
    }
    // Care team keys off the FHIR Location that matches the address the
    // NPD page lists first (when we have it), so the graph and the
    // address shown above it are the same place — not whichever
    // Location FHIR happened to return first.
    const allLocations = result.practices.flatMap((p) => p.locations || []);
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const careLocation =
      (preferredAddressLine &&
        allLocations.find((l) => norm(l.line) === norm(preferredAddressLine))) ||
      allLocations[0] ||
      null;
    if (careLocation?.id) {
      result.careTeamLocation = careLocation;
      result.careTeam = await fetchCareTeamAtLocation(careLocation.id, npi);
    }
  } catch {
    // Swallow — practices/endpoints/ownership/care-team are all
    // additive; the caller still gets the core provider record either
    // way.
  }

  return result;
}


/* ------------------------------------------------------------------ */
/* NPD search API (the source the NPD website renders from)           */
/* ------------------------------------------------------------------ */

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};
const boolOrNull = (v) => {
  if (v === true || v === false) return v;
  if (v == null || v === "") return null;
  const s = String(v).trim().toLowerCase();
  if (["true", "y", "yes", "1"].includes(s)) return true;
  if (["false", "n", "no", "0"].includes(s)) return false;
  return null;
};
const firstStr = (...vals) => vals.map(str).find(Boolean) || null;

// Same rule the NPD site uses: "verified" counts only when it isn't
// "unverified" / "not verified" / "pending" / "not started".
function isVerifiedStatus(s) {
  return !!s && /verified/i.test(s) && !/pending|unverified|not verified|not started/i.test(s);
}

// "PETER K BUCHERT" -> "Peter K. Buchert" (person names only).
function formatDisplayName(raw) {
  if (!raw) return "";
  return toTitleCase(raw)
    .split(" ")
    .map((w) => (w.length === 1 ? `${w}.` : w))
    .join(" ");
}

function normalizeGender(g) {
  const s = str(g)?.toUpperCase();
  if (!s) return null;
  if (s === "M" || s === "MALE") return "male";
  if (s === "F" || s === "FEMALE") return "female";
  return s.toLowerCase();
}

function parseNpdLocation(loc) {
  const zip = str(loc.zip ?? loc.postalCode);
  const zip4 = str(loc.zip4);
  return {
    id: null, // the search API has no FHIR Location id
    line: [loc.addrLineOne, loc.addrLineTwo].map(str).filter(Boolean).join(", "),
    city: str(loc.city) || "",
    state: str(loc.state) || "",
    postalCode: zip || "",
    postalCodeFull: zip ? (zip4 ? `${zip}-${zip4}` : zip) : "",
    country: str(loc.country) || "US",
    county: str(loc.county),
    type: str(loc.locationType)?.toLowerCase() || null, // "practice" | "mailing"
    isFormer: loc.isFormer === true,
    phone: str(loc.phone),
    fax: str(loc.fax),
    website: firstStr(loc.website, loc.practiceWebsite, loc.url, loc.websiteUrl),
    wheelchairAccessible: boolOrNull(loc.isWheelchairAccessible ?? loc.wheelchairAccessible),
    acceptingNewPatients: boolOrNull(loc.acceptsNewPatients ?? loc.acceptingNewPatients),
    officeHours: firstStr(loc.officeHours, loc.hoursOfOperation),
    latitude: typeof loc.latitude === "number" ? loc.latitude : null,
    longitude: typeof loc.longitude === "number" ? loc.longitude : null,
  };
}

function parseNpdContact(c) {
  const value = str(c.contactValue);
  if (!value) return null;
  const ext = str(c.phoneExt);
  return {
    type: str(c.contactType)?.toLowerCase() || "phone", // phone | fax | email
    value,
    ext,
    use: str(c.contactUse)?.toLowerCase() || null,
  };
}

const KNOWN_ENDPOINT_KEYS = new Set([
  "endpointUrl", "endpointType", "endpointTypeDesc", "apiInformationSourceName", "apiDeveloperName",
  "connectionType", "contentType", "fhirVersion", "isActive", "httpResponse", "httpResponseTimeSecond",
]);

function parseNpdEndpoint(ep, ownerName = null) {
  const url = str(ep.endpointUrl) || str(ep.url);
  const active = ep.isActive === true ? true : ep.isActive === false ? false : null;
  return {
    // Full detail, matching the NPD page's "Endpoints" section.
    type: str(ep.endpointType), // "FHIR" | "DIRECT" ...
    // NPD's title for the endpoint, e.g. "Direct Messaging Address".
    title: firstStr(ep.endpointTypeDesc, ep.endpointType) || "Endpoint",
    url,
    informationSource: str(ep.apiInformationSourceName),
    developer: str(ep.apiDeveloperName),
    connectionType: str(ep.connectionType), // e.g. "Oracle Cerner"
    contentType: str(ep.contentType),
    fhirVersion: str(ep.fhirVersion),
    active,
    httpResponse: ep.httpResponse ?? null,
    responseTimeSeconds:
      typeof ep.httpResponseTimeSecond === "number" ? ep.httpResponseTimeSecond : null,
    owner: ownerName, // null = listed on the provider; else the org it's listed under
    // Any field CMS adds later — NPD lists every endpoint field, so do we.
    extra: Object.entries(ep)
      .filter(([k, v]) => !KNOWN_ENDPOINT_KEYS.has(k) && v != null && v !== "" && typeof v !== "object")
      .map(([k, v]) => ({ key: k, value: String(v) })),
    // Legacy keys (ProviderQuickLook / older UI).
    id: url,
    name: str(ep.connectionType) || str(ep.endpointType) || "Endpoint",
    address: url || "",
    status: active === true ? "active" : active === false ? "off" : "unknown",
  };
}

function parseNpdOrganization(org) {
  const name = firstStr(org.organizationName, org.legalBusinessName, org.name, org.displayName);
  const contacts = arr(org.contacts).map(parseNpdContact).filter(Boolean);
  const firstPhone = contacts.find((c) => c.type === "phone")?.value || null;
  const firstFax = contacts.find((c) => c.type === "fax")?.value || null;
  const locations = arr(org.locations)
    .map(parseNpdLocation)
    .filter((l) => !l.isFormer && (l.line || l.city));
  return {
    npi: str(org.organizationNpi),
    name: name || "Organization",
    managingOrgName: str(org.managingOrgName),
    managingOrgId: str(org.managingOrgId),
    type: firstStr(org.organizationType, org.organizationTypeDesc, org.type),
    acceptingPatients: boolOrNull(org.acceptingPatients ?? org.acceptingNewPatients),
    specialties: arr(org.specialties)
      .map((s) => firstStr(s.taxonomyDescription, s.description, s.name))
      .filter(Boolean),
    locations,
    contacts,
    endpoints: arr(org.endpoints).map((e) => parseNpdEndpoint(e, name)).filter((e) => e.url),
    telehealth: arr(org.telehealthPractices).map(parseTelehealth).filter(Boolean),
    clia: arr(org.clia).map(parseClia).filter(Boolean),
    // Location objects with the org's phone/fax filled in, in the shape
    // buildLocationHub() expects.
    legacyLocations: locations.map((l) => ({
      ...l,
      phone: l.phone || firstPhone,
      fax: l.fax || firstFax,
    })),
  };
}

function parseTelehealth(t) {
  const out = {
    practiceName: firstStr(t.telehealthPracticeName, t.practiceName),
    url: firstStr(t.telehealthLocationUrl, t.url),
    acceptingNewPatients: boolOrNull(t.acceptingNewPatients),
    states: arr(t.states ?? t.telehealthStates).map(str).filter(Boolean),
  };
  return out.practiceName || out.url || out.states.length ? out : null;
}

function parseClia(c) {
  const out = {
    certificateNumber: firstStr(c.cliaNumber, c.certificateNumber),
    certificateType: firstStr(c.certificateType, c.type),
    effectiveDate: str(c.effectiveDate),
    isLaboratoryDirector: boolOrNull(c.director ?? c.isLaboratoryDirector),
  };
  return out.certificateNumber || out.certificateType ? out : null;
}

function parseNpdSpecialties(provider) {
  const primaryName = str(provider.primarySpecialty)?.toLowerCase() || null;
  const seen = new Set();
  const list = [];
  arr(provider.specialties).forEach((s) => {
    const raw = firstStr(s.taxonomyDescription, s.description, s.name);
    const code = str(s.taxonomyCode);
    if (!raw && !code) return; // e.g. a bare { medicareSpecialtyCode } row
    const key = (code || raw).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push({
      code,
      display: raw ? toTitleCase(raw) : code,
      shortDescription: str(s.description) ? toTitleCase(s.description) : null,
      definition: str(s.taxonomyDefinition),
      isPrimary:
        s.isPrimary === true || (!!primaryName && !!raw && raw.toLowerCase() === primaryName),
    });
  });
  // Only one primary — the taxonomy that matches `primarySpecialty`
  // wins if the API flagged several.
  const primaries = list.filter((s) => s.isPrimary);
  if (primaries.length > 1 && primaryName) {
    list.forEach((s) => {
      s.isPrimary = s.display.toLowerCase() === primaryName;
    });
  }
  if (!list.some((s) => s.isPrimary) && primaryName) {
    list.unshift({ code: null, display: toTitleCase(primaryName), isPrimary: true });
  }
  return list.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}

function parseNpdLanguages(provider) {
  const seen = new Set();
  return [...arr(provider.languages), ...arr(provider.language)]
    .map((l) => {
      if (typeof l === "string") return { language: str(l), proficiency: null };
      if (l && typeof l === "object") {
        const p = l.proficiency;
        return {
          language: firstStr(l.language, l.name, l.display, l.code),
          proficiency: Number.isFinite(Number(p)) && p !== "" && p != null ? Number(p) : null,
        };
      }
      return null;
    })
    .filter((l) => l?.language && !seen.has(l.language.toLowerCase()) && seen.add(l.language.toLowerCase()));
}

function parseNpdProvider(provider, npi) {
  const isOrg = String(provider.npiType) === "2";
  const specialties = parseNpdSpecialties(provider);
  const credentials = arr(provider.credentials)
    .map((c) => ({ code: str(c.credential), name: str(c.credentialName) }))
    .filter((c) => c.code || c.name);

  const organizations = arr(provider.organizations).map(parseNpdOrganization);
  const ownLocations = arr(provider.locations).map(parseNpdLocation).filter((l) => !l.isFormer);
  const practiceLocations = ownLocations.filter((l) => l.type !== "mailing");
  const mailingAddresses = ownLocations.filter((l) => l.type === "mailing");
  const contacts = arr(provider.contacts).map(parseNpdContact).filter(Boolean);

  const endpoints = arr(provider.endpoints).map((e) => parseNpdEndpoint(e)).filter((e) => e.url);
  const orgEndpoints = organizations.flatMap((o) => o.endpoints);

  const licenses = arr(provider.licenses)
    .map((l) => ({
      number: str(l.licenseNumber),
      state: str(l.state),
      issued: firstStr(l.issueDate, l.issuedDate, l.licenseIssueDate, l.effectiveDate),
      expires: firstStr(l.expirationDate, l.licenseExpirationDate),
      status: firstStr(l.status, l.licenseStatus),
      federalPortability: arr(l.federalPortability).map((f) => (typeof f === "string" ? f : firstStr(f?.name, f?.code))).filter(Boolean),
    }))
    .filter((l) => l.number || l.state);

  const identityStatus =
    firstStr(provider.identityVerificationStatus, provider.identityVerifiedStatus, provider.ial2VerificationStatus) ||
    (provider.verified === true ? "Verified" : "Not started");
  const attestationRaw =
    firstStr(
      provider.npdRecordVerificationStatus,
      provider.npdVerificationStatus,
      provider.recordVerificationStatus,
      provider.verificationStatus
    ) || "Not started";

  const dataNetworks = [
    ...new Set(
      [provider.networkName, provider.dataNetworkName, ...arr(provider.dataNetworks), ...arr(provider.dataNetworkParticipation)]
        .map((n) => (typeof n === "string" ? str(n) : firstStr(n?.networkName, n?.name, n?.displayName)))
        .filter(Boolean)
    ),
  ];

  const PROGRAM_KEYS = [
    "cmsProgramAffiliations", "cmsPrograms", "participatingPrograms", "programAffiliation",
    "programAffiliationDetails", "programAffiliationNames", "programAffiliations", "programParticipation",
    "programParticipations", "programs", "providerProgramAffiliations", "qualityPrograms",
  ];
  const programValue = (v) => {
    if (Array.isArray(v)) return v.flatMap(programValue);
    if (v && typeof v === "object") {
      const n = firstStr(v.programName, v.programDisplayName, v.programDisplay, v.program, v.name, v.display, v.title, v.label, v.description, v.code);
      return n ? [n] : [];
    }
    const n = str(v);
    return n ? [n] : [];
  };
  const programAffiliations = [...new Set(PROGRAM_KEYS.flatMap((k) => programValue(provider[k])))];

  const soleProp = boolOrNull(provider.soleProprietor);
  const separateEntity = boolOrNull(provider.separateBusinessEntity);
  // Same rule and wording as the NPD page; null = NPD shows nothing.
  const businessStructure =
    soleProp === true && separateEntity === true
      ? "I sometimes operate as a sole proprietor for contracting and sometimes I associate with a separate business entity for contracting."
      : separateEntity === true
      ? "I always operate through a separate business entity for insurance contracting."
      : soleProp === true
      ? "I operate exclusively as a sole proprietor for insurance contracting purposes."
      : null;

  const isMedicare = boolOrNull(provider.isMedicareProvider);
  const acceptingNewPatients = boolOrNull(provider.hasAcceptingPatients ?? provider.acceptingNewPatients);

  // Legacy "practices" list for buildLocationHub() / ProviderQuickLook.
  const practices = organizations.map((o, i) => ({
    id: `npd-org-${o.npi || i}`,
    active: true, // the NPD page lists these as current affiliations
    organizationId: null,
    organizationName: o.name,
    organizationNpi: o.npi,
    organizationType: null,
    organizationPartOfRef: null,
    locations: o.legacyLocations,
  }));
  if (practiceLocations.length) {
    const firstPhone = contacts.find((c) => c.type === "phone")?.value || null;
    const firstFax = contacts.find((c) => c.type === "fax")?.value || null;
    practices.push({
      id: "npd-own-locations",
      active: true,
      organizationName: "Not associated with an organization",
      organizationNpi: null,
      organizationType: null,
      locations: practiceLocations.map((l) => ({ ...l, phone: l.phone || firstPhone, fax: l.fax || firstFax })),
    });
  }

  const allContacts = [...contacts, ...organizations.flatMap((o) => o.contacts)];

  return {
    source: "npd",
    id: null, // FHIR id filled in by fetchProviderFromNdh if FHIR also has the record
    npi: str(provider.id) || npi,
    npiType: isOrg ? "organization" : "individual",
    name: isOrg ? str(provider.displayName) || "Unknown organization" : formatDisplayName(provider.displayName) || "Unknown provider",
    nameRaw: str(provider.displayName),
    otherNames: arr(provider.names)
      .map((n) => ({
        name: firstStr(n.organizationName, [n.firstName, n.middleName, n.lastName].map(str).filter(Boolean).join(" ")),
        type: str(n.nameType),
        isFormer: n.isFormer === true,
      }))
      .filter((n) => n.name),
    active: provider.deactivated !== true,
    gender: normalizeGender(provider.gender),
    credentials,
    credential: credentials.map((c) => c.code || c.name).join(", "), // legacy string, e.g. "MD"
    specialties,
    primarySpecialty: specialties.find((s) => s.isPrimary)?.display || null,
    medicareSpecialtyCodes: arr(provider.specialties).map((s) => str(s.medicareSpecialtyCode)).filter(Boolean),
    languages: parseNpdLanguages(provider),
    enumerationDate: str(provider.enumerationDate)?.slice(0, 10) || null,
    soleProprietor: boolOrNull(provider.soleProprietor),
    separateBusinessEntity: boolOrNull(provider.separateBusinessEntity),
    organizationSubpart: boolOrNull(provider.organizationSubpart),
    isPrimaryCareClinician: boolOrNull(provider.isPrimaryCareClinician ?? provider.primaryCareClinician ?? provider.isPcp),
    acceptingNewPatients, // raw NPD flag (NPD itself doesn't display it)
    acceptingPatients: acceptingNewPatients === true ? { value: true, label: "Yes", source: "NPD" } : null, // resolved below with FHIR
    isMedicareProvider: isMedicare,
    insurance: isMedicare === true ? ["Medicare"] : [],
    verification: {
      identityStatus,
      identityVerified: isVerifiedStatus(identityStatus),
      // NPD only shows these dates once the status is actually verified.
      identityDate: isVerifiedStatus(identityStatus)
        ? firstStr(provider.identityVerificationDate, provider.identityVerifiedDate, provider.ial2VerifiedAt, provider.verifiedAt)
        : null,
      attestationStatus: attestationRaw,
      attestationComplete: isVerifiedStatus(attestationRaw),
      attestationDate: isVerifiedStatus(attestationRaw)
        ? firstStr(provider.npdRecordVerificationDate, provider.npdVerificationTime, provider.recordVerificationDate, provider.verificationDate, provider.verifiedDate, provider.lastVerified)
        : null,
    },
    licenses,
    stateLicense: licenses[0]
      ? { number: licenses[0].number, state: licenses[0].state, issued: licenses[0].issued, expires: licenses[0].expires }
      : null,
    boardCertifications: arr(provider.boardCertificationDetails ?? provider.boardCertifications)
      .map((b) => ({
        organization: firstStr(b.boardCertificationOrganization, b.organization, b.issuingOrganization),
        name: firstStr(b.boardCertificationName, b.name, b.certification),
        number: firstStr(b.boardCertificationNumber, b.number, b.certificateNumber),
        expirationDate: firstStr(b.boardCertificationExpirationDate, b.expirationDate),
      }))
      .filter((b) => b.organization || b.name || b.number),
    telehealth: arr(provider.telehealthPractices).map(parseTelehealth).filter(Boolean),
    dea: arr(provider.deaRegistrations)
      .map((d) => ({
        state: str(d.state),
        schedules: arr(d.schedules ?? d.schedulesAuthorized).map(str).filter(Boolean),
        expirationDate: str(d.expirationDate),
      }))
      .filter((d) => d.state || d.schedules.length || d.expirationDate),
    clia: arr(provider.cliaCertificates ?? provider.cliaRegistrations).map(parseClia).filter(Boolean),
    affiliations: arr(provider.affiliations)
      .map((a) => ({
        name: str(a.affiliatedOrganizationName),
        type: str(a.affiliatedOrganizationType),
        relation: str(a.affiliationType),
      }))
      .filter((a) => a.name),
    organizations,
    practiceLocations, // "not associated with an organization record"
    mailingAddresses,
    contacts, // provider-level phones/fax/email not tied to an org
    endpoints, // provider-level — what the NPD page lists under "Endpoints"
    orgEndpoints, // listed under an affiliated organization
    dataNetworks,
    programAffiliations,
    businessStructure,
    officeHours: firstStr(provider.officeHours, provider.hoursOfOperation),
    recordHistory: {
      npiEnumerated: str(provider.enumerationDate)?.slice(0, 10) || null, // source: NPPES
      recordLastUpdated: str(provider.lastVerified), // source: NPD
      sourceSystems: ["NPD"],
      fhirLastUpdated: null, // filled in from FHIR meta.lastUpdated below
    },
    authorizedOfficials: arr(provider.authorizedOfficials)
      .map((a) => ({
        name: firstStr(a.name, [a.firstName, a.middleName, a.lastName].map(str).filter(Boolean).join(" ")),
        title: firstStr(a.title, a.titleOrPosition, a.position),
        phone: firstStr(a.phone, a.telephoneNumber, a.contactValue),
      }))
      .filter((a) => a.name || a.title),
    // Legacy fields used elsewhere (ProviderQuickLook, buildLocationHub).
    practices,
    addresses: [...organizations.flatMap((o) => o.locations), ...ownLocations],
    phones: allContacts.filter((c) => c.type === "phone").map((c) => c.value),
    faxes: allContacts.filter((c) => c.type === "fax").map((c) => c.value),
    flags: {
      identityVerified: provider.verified === true ? true : isVerifiedStatus(identityStatus) ? true : null,
      medicareEnrolled: isMedicare,
      exclusionListed: null, // FHIR-only; filled in below when available
      dataNetworkAligned: dataNetworks.length > 0 || endpoints.some((e) => e.active) ? true : null,
    },
    ownershipChain: [],
    careTeam: [],
    careTeamLocation: null,
    lastUpdated: str(provider.lastVerified),
    rawRecordUrl: `https://directory.cms.gov/provider/${str(provider.id) || npi}`,
  };
}

async function fetchNpdPractitioner(npi) {
  let response;
  try {
    response = await fetch(`${NPD_API_BASE_URL}/practitioners/${encodeURIComponent(npi)}`, {
      headers: { Accept: "application/json" },
    });
  } catch (networkErr) {
    const err = new Error("Couldn't reach the CMS directory — it may be temporarily unavailable.");
    err.code = "NETWORK_ERROR";
    err.cause = networkErr;
    throw err;
  }
  if (response.status === 404) {
    const err = new Error("This provider isn't listed in the CMS National Directory.");
    err.code = "NOT_FOUND";
    throw err;
  }
  if (!response.ok) {
    const err = new Error(`CMS directory returned HTTP ${response.status}.`);
    err.code = "SERVER_ERROR";
    throw err;
  }
  const body = await response.json();
  const value = body?.value || {};
  if (value.deactivated === true || value.isDeactivated === true) {
    const err = new Error(`NPI ${npi} has been deactivated and is no longer in service.`);
    err.code = "NOT_FOUND";
    throw err;
  }
  const provider = value.provider;
  if (value.found === false || !provider) {
    const err = new Error("This provider isn't listed in the CMS National Directory.");
    err.code = "NOT_FOUND";
    throw err;
  }
  return parseNpdProvider(provider, npi);
}

/** Organization detail from the same NPD search API (e.g. to open an affiliated org). */
export async function fetchOrganizationFromNpd(orgNpi) {
  const response = await fetch(`${NPD_API_BASE_URL}/organizations/${encodeURIComponent(orgNpi)}`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`CMS directory returned HTTP ${response.status}.`);
  const value = (await response.json())?.value || {};
  if (value.found === false || !value.provider) {
    const err = new Error("This organization isn't listed in the CMS National Directory.");
    err.code = "NOT_FOUND";
    throw err;
  }
  return parseNpdProvider(value.provider, orgNpi);
}

/* ------------------------------------------------------------------ */
/* FHIR Practitioner                                                   */
/* ------------------------------------------------------------------ */

async function fetchFhirPractitioner(npi) {
  const qualifiedIdentifier = `${NPI_IDENTIFIER_SYSTEM}|${npi}`;
  const url = `${NDH_BASE_URL}/Practitioner?identifier=${encodeURIComponent(qualifiedIdentifier)}`;

  let response;
  try {
    response = await fetch(url, { headers: FHIR_HEADERS });
  } catch (networkErr) {
    const err = new Error("Couldn't reach the CMS directory — it may be temporarily unavailable.");
    err.code = "NETWORK_ERROR";
    err.cause = networkErr;
    throw err;
  }
  if (response.status === 404) {
    const err = new Error("This provider isn't listed in the CMS National Directory.");
    err.code = "NOT_FOUND";
    throw err;
  }
  if (!response.ok) {
    const err = new Error(`CMS directory returned HTTP ${response.status}.`);
    err.code = "SERVER_ERROR";
    throw err;
  }
  const bundle = await response.json();
  const parsed = parsePractitionerResource((bundle.entry || [])[0]?.resource);
  if (!parsed) {
    const err = new Error("This provider isn't listed in the CMS National Directory.");
    err.code = "NOT_FOUND";
    throw err;
  }
  return parsed;
}

// Gives a FHIR-only record the same shape as an NPD one, so the UI has
// one code path. Only fills what FHIR actually has.
function fhirToUnifiedShape(parsed) {
  const endpoints = (parsed.endpoints || []).map((e) => ({
    type: null,
    url: e.address || null,
    informationSource: null,
    developer: null,
    connectionType: e.connectionType || null,
    contentType: null,
    fhirVersion: null,
    active: e.status === "active" ? true : e.status ? false : null,
    httpResponse: null,
    responseTimeSeconds: null,
    owner: null,
    title: e.connectionType || e.name || "Endpoint",
    extra: [],
    ...e,
  }));
  const organizations = (parsed.practices || []).map((p) => ({
    npi: p.organizationNpi,
    name: p.organizationName,
    managingOrgName: null,
    acceptingPatients: null,
    specialties: [],
    locations: p.locations || [],
    contacts: [
      ...new Map(
        (p.locations || [])
          .flatMap((l) => [
            l.phone && { type: "phone", value: l.phone },
            l.fax && { type: "fax", value: l.fax },
          ])
          .filter(Boolean)
          .map((c) => [`${c.type}${c.value}`, c])
      ).values(),
    ],
    endpoints: [],
    telehealth: [],
    clia: [],
    active: p.active,
  }));
  return {
    ...parsed,
    source: "fhir",
    npiType: "individual",
    otherNames: [],
    credentials: parsed.credential ? [{ code: null, name: parsed.credential }] : [],
    specialties: (parsed.specialties || []).map((s) => ({ ...s, isPrimary: false })),
    primarySpecialty: null, // FHIR doesn't flag a primary taxonomy
    languages: [],
    soleProprietor: null,
    isPrimaryCareClinician: null,
    acceptingNewPatients: null,
    isMedicareProvider: parsed.flags?.medicareEnrolled ?? null,
    insurance: parsed.flags?.medicareEnrolled === true ? ["Medicare"] : [],
    verification: {
      identityStatus: parsed.flags?.identityVerified === true ? "Verified" : "Not started",
      identityVerified: parsed.flags?.identityVerified === true,
      attestationStatus: null,
      attestationComplete: null,
    },
    licenses: parsed.stateLicense ? [{ ...parsed.stateLicense, status: null, federalPortability: [] }] : [],
    boardCertifications: [],
    telehealth: [],
    dea: [],
    clia: [],
    affiliations: [],
    organizations,
    practiceLocations: [],
    mailingAddresses: [],
    contacts: [
      ...(parsed.phones || []).map((v) => ({ type: "phone", value: v })),
      ...(parsed.faxes || []).map((v) => ({ type: "fax", value: v })),
    ],
    endpoints,
    orgEndpoints: [],
    dataNetworks: [],
    programAffiliations: [],
    businessStructure: null,
    officeHours: null,
    authorizedOfficials: [],
    acceptingPatients: (() => {
      const a = (parsed.practices || []).map((p) => p.acceptingPatients).find(Boolean);
      return a?.display ? { value: a.code !== "nopt", label: a.display, source: "FHIR" } : null;
    })(),
    recordHistory: {
      npiEnumerated: parsed.enumerationDate || null,
      recordLastUpdated: null,
      sourceSystems: ["FHIR"],
      fhirLastUpdated: parsed.lastUpdated || null,
    },
  };
}

/**
 * Fetches everything the CMS directory has on one NPI — the same data
 * the public NPD provider page shows, plus the FHIR-only extras (care
 * team at this location, ownership chain, exclusion flag).
 *
 * Throws on total failure — the caller shows a state matching err.code:
 *  - "NOT_FOUND": the directory answered; this NPI isn't in it.
 *  - anything else: connectivity/server problem.
 *
 * The NPD search API and FHIR are queried in parallel. NPD wins when
 * it answers (it's what the website shows); FHIR fills in the extras,
 * and is used on its own only if the NPD API is unavailable.
 */
export async function fetchProviderFromNdh(npi) {
  if (!npi) throw new Error("Missing NPI");

  const [npdRes, fhirRes] = await Promise.allSettled([
    fetchNpdPractitioner(npi),
    fetchFhirPractitioner(npi),
  ]);
  const npd = npdRes.status === "fulfilled" ? npdRes.value : null;
  const fhir = fhirRes.status === "fulfilled" ? fhirRes.value : null;

  if (!npd && !fhir) {
    const npdErr = npdRes.reason;
    const fhirErr = fhirRes.reason;
    // Only report "not found" when neither source had a connectivity
    // problem — otherwise a flaky network would look like a missing NPI.
    if (npdErr?.code === "NOT_FOUND" && fhirErr?.code === "NOT_FOUND") throw npdErr;
    throw [npdErr, fhirErr].find((e) => e && e.code !== "NOT_FOUND") || npdErr || fhirErr;
  }

  if (npd) {
    if (fhir) {
      const primaryLine =
        npd.organizations[0]?.locations[0]?.line || npd.practiceLocations[0]?.line || null;
      const extras = await fetchPracticesAndEndpoints(fhir.id, npd.npi, primaryLine, {
        includeEndpoints: false,
      });
      npd.id = fhir.id;
      npd.careTeam = extras.careTeam;
      npd.careTeamLocation = extras.careTeamLocation;
      npd.ownershipChain = extras.ownershipChain;
      npd.fhirPractices = extras.practices; // raw FHIR view, for debugging/comparison
      npd.flags.exclusionListed = fhir.flags?.exclusionListed ?? null;
      if (npd.flags.identityVerified == null && fhir.flags?.identityVerified != null) {
        npd.flags.identityVerified = fhir.flags.identityVerified;
      }
      if (npd.flags.dataNetworkAligned == null) npd.flags.dataNetworkAligned = fhir.flags?.dataNetworkAligned ?? null;
      npd.lastUpdated = npd.lastUpdated || fhir.lastUpdated;
      npd.recordHistory.fhirLastUpdated = fhir.lastUpdated || null;
      if (!npd.acceptingPatients) {
        const fhirAccepting = (extras.practices || []).map((p) => p.acceptingPatients).find(Boolean);
        if (fhirAccepting?.display) {
          npd.acceptingPatients = {
            value: fhirAccepting.code === "nopt" ? false : true,
            label: fhirAccepting.display,
            source: "FHIR",
          };
        }
      }
    }
    return npd;
  }

  // FHIR-only fallback (NPD search API unreachable).
  const extras = await fetchPracticesAndEndpoints(fhir.id, fhir.npi);
  Object.assign(fhir, extras);
  return fhirToUnifiedShape(fhir);
}